<template>
  <div class="data-provisions-screen">
    <div class="data-provisions-header">
      <h3>{{ t('accountInfo.dataProvisions.title') }}</h3>
      <p class="data-provisions-description">{{ t('accountInfo.dataProvisions.description') }}</p>
    </div>
    
    <div v-if="isLoading" class="loading-state">
      <p>{{ t('labels.Loading') }}...</p>
    </div>
    
    <div v-else-if="error" class="error-message">
      <p>{{ error }}</p>
      <button @click="loadDataProvisions" class="retry-button">
        {{ t('buttons.Retry') }}
      </button>
    </div>
    
    <div v-else-if="termsItems.length > 0" class="contracts-container">
      <div class="contract-card" v-for="item in termsItems" :key="item.termsId">
        <div class="card-header">
          <h4 class="contract-title">{{ item.termsDescription }}</h4>
          <span :class="getStatusClass(item)" class="status-badge">
            {{ getStatusText(item) }}
          </span>
        </div>
        
        <div class="card-content">
          <div class="info-row">
            <span class="info-label">{{ t('accountInfo.dataProvisions.termsId') }}:</span>
            <span class="info-value">{{ item.termsId }}</span>
          </div>
          
          <div class="info-row">
            <span class="info-label">{{ t('accountInfo.dataProvisions.version') }}:</span>
            <span class="info-value">{{ item.versionNumber }}</span>
          </div>
          
          <div class="info-row">
            <span class="info-label">{{ t('accountInfo.dataProvisions.acknowledgedDate') }}:</span>
            <span class="info-value">{{ formatDate(item.acknowledgeDate || '') || 'N/A' }}</span>
          </div>
        </div>
      </div>
    </div>
    
    <div v-else class="no-contracts">
      <div class="empty-state">
        <FontAwesomeIcon :icon="faFileContract" class="empty-state-icon" />
        <h5>{{ t('accountInfo.dataProvisions.noContracts') }}</h5>
        <p>{{ t('accountInfo.dataProvisions.noContractsDescription') }}</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useLogger } from 'vue-logger-plugin'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faFileContract } from '@fortawesome/free-solid-svg-icons'
import { PwsProfileTermsGetList } from '../../api/ConsultantCorner'
import { GetBeeNumberAuthenicatedUser } from '../../stores/SettingsStore'

interface ITermsResponse {
  beeNumber: number
  items: ITermsItem[]
}

interface ITermsItem {
  termsId: string
  termsDescription: string
  versionNumber: number
  versionName: string
  codeIdAckAction?: {
    codeId: string
    name: string
    localizedValue: string
    categoryName: string
    categoryId: string
  }
  acknowledgeDate?: string
  mustBeSignedByDate: string
}

const { t } = useI18n()
const logger = useLogger()

const termsItems = ref<ITermsItem[]>([])
const isLoading = ref<boolean>(false)
const error = ref<string>('')

const loadDataProvisions = async () => {
  try {
    isLoading.value = true
    error.value = ''
    
    const beeNumber = await GetBeeNumberAuthenicatedUser()
    if (!beeNumber) {
      logger.error('No authenticated user bee number found for data provisions')
      error.value = t('accountInfo.dataProvisions.errorNoUser')
      return
    }

    logger.debug('Loading data provisions for bee number:', beeNumber)
    const response = await PwsProfileTermsGetList({ beeNumber }) as unknown as ITermsResponse
    
    if (response?.items) {
      termsItems.value = response.items
      logger.debug('Data provisions loaded:', termsItems.value.length, 'terms')
    } else {
      termsItems.value = []
      logger.warn('No terms found in response')
    }
  } catch (err) {
    console.error('Error loading data provisions:', err)
    error.value = t('accountInfo.dataProvisions.errorLoading')
  } finally {
    isLoading.value = false
  }
}

onMounted(async () => {
  await loadDataProvisions()
})


// Helper functions for status display
function getStatusText(item: ITermsItem): string {
  if (item.codeIdAckAction?.codeId === 'ACCEPT') {
    return t('accountInfo.dataProvisions.statusAccepted')
  } else if (item.codeIdAckAction?.codeId === 'DECLINE') {
    return t('accountInfo.dataProvisions.statusDeclined')
  } else {
    return t('accountInfo.dataProvisions.statusPending')
  }
}

function getStatusClass(item: ITermsItem): string {
  if (item.codeIdAckAction?.codeId === 'ACCEPT') {
    return 'status-accepted'
  } else if (item.codeIdAckAction?.codeId === 'DECLINE') {
    return 'status-declined'
  } else {
    return 'status-pending'
  }
}

const formatDate = (dateString: string): string => {
  if (!dateString) return ''
  
  try {
    const date = new Date(dateString)
    return date.toLocaleDateString()
  } catch {
    return dateString
  }
}
</script>

<style lang="scss" scoped>
.data-provisions-screen {
  width: 100%;
  padding: $card-padding;
}

.data-provisions-header {
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.data-provisions-header h3 {
  @include typography-h4;
  margin: 0 0 12px 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
}

.data-provisions-description {
  @include typography-small;
  margin: 0;
  color: #666;
}

.loading-state {
  @include typography-body;
  text-align: center;
  padding: $spacing-10;
  color: #666;
}

.error-message {
  text-align: center;
  padding: $spacing-10;
  color: #dc3545;
}

.retry-button {
  margin-top: 16px;
  padding: $spacing-2 $spacing-4;
  background-color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  color: white;
  border: none;
  @include typography-small;
  border-radius: $button-radius-lg;
  cursor: pointer;
}

.retry-button:hover {
  background-color: var(--buttonPrimaryHoverColor, #145258);
}

.contracts-container {
  display: flex;
  flex-direction: column;
  gap: $gap-md;
}

.contract-card {
  background: white;
  border: 1px solid #e9ecef;
  border-radius: $radius-lg;
  box-shadow: 0 2px 8px rgba(black, 0.1);
  overflow: hidden;
  transition: all 0.2s ease;
}

.contract-card:hover {
  box-shadow: 0 4px 12px rgba(black, 0.2);
  transform: translateY(-2px);
}

.card-header {
  background-color: var(--vt-c-white-soft);
  padding: $card-padding;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.contract-title {
  @include typography-body-large;
  @include font-weight-semibold;
  margin: 0;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  flex: 1;
  margin-right: 16px;
}

.status-badge {
  @include typography-caption;
  @include font-weight-semibold;
  padding: $spacing-2 $spacing-3;
  border-radius: $radius-2xl;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-content {
  padding: $card-padding;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: $spacing-2 0;
  border-bottom: 1px solid var(--vt-c-white-soft);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  @include typography-small;
  @include font-weight-semibold;
  color: #666;
}

.info-value {
  @include typography-small;
  color: #333;
  text-align: right;
}

/* Mobile responsive design */
@media (max-width: 768px) {
  .data-provisions-screen {
    padding: $spacing-4;
  }
  
  .data-provisions-header h3 {
    @include typography-body-large;
  }
  
  .data-provisions-header p {
    @include typography-small;
  }
  
  .contracts-container {
    gap: $spacing-3;
  }
  
  .card-header {
    padding: $spacing-4;
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-3;
  }
  
  .contract-title {
    @include typography-body;
    margin-right: 0;
  }
  
  .card-content {
    padding: $spacing-4;
  }
  
  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: $spacing-1;
  }
  
  .info-value {
    @include font-weight-medium;
    text-align: left;
  }
}

.no-contracts {
  text-align: center;
  padding: $spacing-16 $spacing-5;
}

.empty-state {
  color: #666;
}

.empty-state-icon {
  @include typography-display;
  color: var(--buttonPrimaryColor, var(--vt-c-teal-dark));
  margin-bottom: 1rem;
}

.empty-state h5 {
  margin-bottom: 12px;
  color: #666;
}

.empty-state p {
  @include typography-small;
  color: #888;
  max-width: 400px;
  margin: 0 auto;
}

.status-accepted {
  color: #28a745;
  background-color: #d4edda;
  border: 1px solid #c3e6cb;
}

.status-declined {
  color: #dc3545;
  background-color: #f8d7da;
  border: 1px solid #f5c6cb;
}

.status-pending {
  color: #856404;
  background-color: #fff3cd;
  border: 1px solid #ffeaa7;
}

</style>
